import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Info } from "lucide-react";
import toast from "react-hot-toast";
import InvestmentMonthWindowsGrid from "../InvestmentMonthWindowsGrid";
import DeclarationWorkflowBlock from "./DeclarationWorkflowBlock";
import {
  DEFAULT_WORKFLOW,
  normalizeWorkflow,
  validateWorkflow,
} from "./declarationWorkflowDefaults";
import {
  buildInvestmentWindowsPayload,
  createDefaultInvestmentWindows,
  normalizeWindowsFromSettings,
} from "../../../utils/investmentDeclarationWindows";

const PAYMENT_MODES = [
  { value: "PAYROLL", label: "Through payroll only" },
  { value: "OUT_OF_CYCLE", label: "Out-of-cycle / separate payment only" },
  { value: "BOTH", label: "Both (payroll or out-of-cycle)" },
];

const DEFAULT_OPT_IN_POLICY = {
  allowEmployeeOptOut: true,
  maxChangesPerComponentPerFy: 2,
  cooldownDays: 30,
  lockAfterFirstApprovedClaim: true,
  lockAfterPayrollPosting: true,
  allowAmountDecreaseToClaimedFloor: true,
  allowAmountIncreaseInWindow: true,
  blockOptOutIfPendingClaims: true,
};

function normalizeOptInPolicy(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    allowEmployeeOptOut: src.allowEmployeeOptOut !== false,
    maxChangesPerComponentPerFy: Math.max(
      0,
      Number(src.maxChangesPerComponentPerFy) || 2
    ),
    cooldownDays: Math.max(0, Number(src.cooldownDays) || 30),
    lockAfterFirstApprovedClaim: src.lockAfterFirstApprovedClaim !== false,
    lockAfterPayrollPosting: src.lockAfterPayrollPosting !== false,
    allowAmountDecreaseToClaimedFloor:
      src.allowAmountDecreaseToClaimedFloor !== false,
    allowAmountIncreaseInWindow: src.allowAmountIncreaseInWindow !== false,
    blockOptOutIfPendingClaims: src.blockOptOutIfPendingClaims !== false,
  };
}

function normalizePaymentMode(raw) {
  const mode = String(raw || "PAYROLL").toUpperCase();
  return mode === "OUT_OF_CYCLE" || mode === "BOTH" ? mode : "PAYROLL";
}

function defaultClaimWindows() {
  const base = createDefaultInvestmentWindows();
  Object.keys(base.months || {}).forEach((m) => {
    base.months[m] = {
      ...base.months[m],
      fromDay: 1,
      toDay: 20,
      enabled: true,
      audience: "all",
    };
  });
  return base;
}

function cloneWindows(windows) {
  return JSON.parse(JSON.stringify(windows || createDefaultInvestmentWindows()));
}

/**
 * Payroll → Settings → Reimbursements
 * Extends existing Flexi / FBP settings; company-wide payment + approval workflow.
 */
const ReimbursementSettings = forwardRef(function ReimbursementSettings(
  {
    darkMode = false,
    settings = null,
    canEdit = true,
    saving = false,
    onSave,
  },
  ref
) {
  const [enableFlexiBenefits, setEnableFlexiBenefits] = useState(false);
  const [flexiEffectiveFrom, setFlexiEffectiveFrom] = useState("");
  const [flexiMirrorInvestment, setFlexiMirrorInvestment] = useState(true);
  const [declarationSourceWindows, setDeclarationSourceWindows] = useState(
    createDefaultInvestmentWindows
  );
  const [flexiDeclarationWindows, setFlexiDeclarationWindows] = useState(
    createDefaultInvestmentWindows
  );
  const [flexiClaimWindows, setFlexiClaimWindows] = useState(defaultClaimWindows);
  const [paymentMode, setPaymentMode] = useState("PAYROLL");
  const [reimbursementWorkflow, setReimbursementWorkflow] = useState(DEFAULT_WORKFLOW);
  const [optInPolicy, setOptInPolicy] = useState(DEFAULT_OPT_IN_POLICY);
  const [showInfo, setShowInfo] = useState(false);

  const loadFromSettings = (apiSettings) => {
    if (!apiSettings) return;
    setEnableFlexiBenefits(!!apiSettings.isFlexiBenefitsEnabled);
    setFlexiEffectiveFrom(
      apiSettings.flexiBenefitsEffectiveFrom
        ? String(apiSettings.flexiBenefitsEffectiveFrom).slice(0, 10)
        : ""
    );
    const mirror = apiSettings.flexiMirrorInvestmentWindows !== false;
    setFlexiMirrorInvestment(mirror);

    const declarationWin = normalizeWindowsFromSettings(
      apiSettings.investmentWindows,
      "investment"
    );
    setDeclarationSourceWindows(declarationWin);

    const customOptIn = normalizeWindowsFromSettings(
      apiSettings.flexiDeclarationWindows || apiSettings.investmentWindows,
      "investment"
    );
    setFlexiDeclarationWindows(customOptIn);

    const claimWin = normalizeWindowsFromSettings(
      apiSettings.flexiClaimWindows,
      "investment"
    );
    const withClaimDefaults = cloneWindows(claimWin);
    Object.keys(withClaimDefaults.months || {}).forEach((m) => {
      withClaimDefaults.months[m] = {
        ...withClaimDefaults.months[m],
        fromDay: withClaimDefaults.months[m]?.fromDay ?? 1,
        toDay: withClaimDefaults.months[m]?.toDay ?? 20,
      };
    });
    setFlexiClaimWindows(withClaimDefaults);
    setPaymentMode(normalizePaymentMode(apiSettings.reimbursementPaymentMode));
    setReimbursementWorkflow(normalizeWorkflow(apiSettings.reimbursementWorkflow));
    setOptInPolicy(normalizeOptInPolicy(apiSettings.flexiOptInPolicy));
  };

  useEffect(() => {
    if (settings) loadFromSettings(settings);
  }, [settings]);

  const optInWindows = useMemo(
    () => (flexiMirrorInvestment ? declarationSourceWindows : flexiDeclarationWindows),
    [flexiMirrorInvestment, declarationSourceWindows, flexiDeclarationWindows]
  );

  const setMirrorMode = (mirror) => {
    if (!canEdit) return;
    if (!mirror && flexiMirrorInvestment) {
      // Switching to custom: seed from current Declaration windows so user edits from known base
      setFlexiDeclarationWindows(cloneWindows(declarationSourceWindows));
    }
    setFlexiMirrorInvestment(mirror);
  };

  useImperativeHandle(ref, () => ({
    loadFromSettings,
    validate: () => {
      if (enableFlexiBenefits && !flexiEffectiveFrom) {
        return {
          ok: false,
          message: "Set an effective date before enabling Reimbursements / Flexi.",
        };
      }
      if (enableFlexiBenefits) {
        const wf = validateWorkflow(
          reimbursementWorkflow,
          "Reimbursement approval workflow"
        );
        if (!wf.ok) return wf;
      }
      return { ok: true };
    },
    buildPayload: () => {
      const payload = {
        isFlexiBenefitsEnabled: enableFlexiBenefits,
        flexiBenefitsEffectiveFrom: flexiEffectiveFrom || null,
        flexiMirrorInvestmentWindows: flexiMirrorInvestment,
        flexiClaimWindows: buildInvestmentWindowsPayload(flexiClaimWindows),
        reimbursementPaymentMode: normalizePaymentMode(paymentMode),
        reimbursementWorkflow: normalizeWorkflow(reimbursementWorkflow),
        flexiOptInPolicy: normalizeOptInPolicy(optInPolicy),
      };
      if (!flexiMirrorInvestment) {
        payload.flexiDeclarationWindows = buildInvestmentWindowsPayload(
          flexiDeclarationWindows
        );
      }
      return payload;
    },
  }));

  const selectCls = `w-full px-2 py-1.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 disabled:opacity-50 ${
    darkMode
      ? "bg-gray-600 border-gray-500 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;
  const sectionShell = `rounded-2xl border p-4 ${
    darkMode ? "border-gray-600 bg-gray-700/40" : "border-gray-200 bg-white"
  }`;
  const mutedBox = darkMode
    ? "border-gray-500 bg-gray-800/60 text-gray-200"
    : "border-gray-200 bg-gray-50 text-gray-700";
  const toggleCls =
    "w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-redhalo-red/35 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-redhalo-red";

  const resetSection = () => {
    if (settings) loadFromSettings(settings);
    else {
      setEnableFlexiBenefits(false);
      setFlexiEffectiveFrom("");
      setFlexiMirrorInvestment(true);
      setPaymentMode("PAYROLL");
      setReimbursementWorkflow(DEFAULT_WORKFLOW);
      setOptInPolicy(DEFAULT_OPT_IN_POLICY);
      setFlexiClaimWindows(defaultClaimWindows());
    }
    toast.success("Reimbursement settings reset");
  };

  const modeBtn = (active) =>
    `rounded-full px-3 py-1.5 text-xs font-semibold ${
      active
        ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
        : darkMode
          ? "text-gray-300 hover:bg-gray-700"
          : "bg-white text-gray-700 hover:bg-gray-100"
    }`;

  return (
    <div className={`mb-5 ${sectionShell}`}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-2 min-w-0">
          <div className="min-w-0">
            <h4 className="text-base font-medium">Reimbursements / Flexi</h4>
          </div>
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            className="mt-0.5 shrink-0 rounded-full p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted)/0.35)] hover:text-[hsl(var(--ds-fg))]"
            aria-label="Reimbursement settings information"
            title="Information"
          >
            <Info size={16} strokeWidth={1.75} />
          </button>
        </div>
        <label className="relative inline-flex cursor-pointer items-center shrink-0">
          <input
            type="checkbox"
            checked={enableFlexiBenefits}
            onChange={(e) => setEnableFlexiBenefits(e.target.checked)}
            disabled={!canEdit}
            className="peer sr-only"
          />
          <div className={toggleCls} />
        </label>
      </div>

      {showInfo && (
        <div className={`mb-4 rounded-xl border px-3 py-2.5 text-xs leading-relaxed ${mutedBox}`}>
          <p className="mb-1 font-medium">What this controls</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>
              <strong>Flexi opt-in windows</strong> — when employees may declare Flexi amounts.
              Choose Same as Declaration (locked to Declaration tab) or Custom (edit this grid).
            </li>
            <li>
              <strong>Claim windows</strong> — separate month days when employees may submit
              reimbursement claims (often 1–20).
            </li>
            <li>
              <strong>Opt-in / opt-out policy</strong> — mid-FY revoke/change limits, cool-down,
              and lock after claim or payroll. Does not rewrite Special Allowance on declare.
            </li>
            <li>
              <strong>Payment treatment</strong> and <strong>approval workflow</strong> are
              company-wide for Flexi claims.
            </li>
          </ul>
        </div>
      )}

      {enableFlexiBenefits && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium">Effective from</label>
              <input
                type="date"
                value={flexiEffectiveFrom}
                disabled={!canEdit}
                onChange={(e) => setFlexiEffectiveFrom(e.target.value)}
                className={selectCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Payment treatment</label>
              <select
                value={paymentMode}
                disabled={!canEdit}
                onChange={(e) => setPaymentMode(e.target.value)}
                className={selectCls}
              >
                {PAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Flexi opt-in — one grid; mirror locks it */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h5 className="text-sm font-medium">Flexi opt-in windows</h5>
              <div className="inline-flex flex-wrap gap-1 rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-1">
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setMirrorMode(true)}
                  className={modeBtn(flexiMirrorInvestment)}
                >
                  Same as Declaration
                </button>
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setMirrorMode(false)}
                  className={modeBtn(!flexiMirrorInvestment)}
                >
                  Custom dates
                </button>
              </div>
            </div>

            <div className={`rounded-xl border px-3 py-2 text-[11px] leading-relaxed ${mutedBox}`}>
              {flexiMirrorInvestment ? (
                <>
                  Following <strong>Payroll → Settings → Declaration</strong> month windows
                  (read-only here). Switch to <strong>Custom dates</strong> to edit Flexi
                  opt-in separately — the same grid unlocks for editing.
                </>
              ) : (
                <>
                  Editing Flexi opt-in windows only (does not change the Declaration tab).
                  Starts from Declaration values when you switch to Custom; adjust months as
                  needed.
                </>
              )}
            </div>

            <InvestmentMonthWindowsGrid
              kind="investment"
              darkMode={darkMode}
              windows={optInWindows}
              locked={flexiMirrorInvestment || !canEdit}
              onUpdateMonth={(monthNum, patch) => {
                if (flexiMirrorInvestment) return;
                setFlexiDeclarationWindows((prev) => ({
                  ...prev,
                  months: {
                    ...(prev.months || {}),
                    [monthNum]: { ...(prev.months?.[monthNum] || {}), ...patch },
                  },
                }));
              }}
              onOpenSelection={() => {}}
              onViewSelected={() => {}}
              selectionTargetMonth={null}
              selectCls={selectCls}
            />
          </div>

          {/* Opt-in / opt-out policy */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium">Opt-in / opt-out policy</h5>
            <p className={`text-[11px] ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Controls mid-FY declaration thrashing. Opt-in amount does not rewrite the salary
              structure — CTC ceiling and Special Allowance are set when HR configures the
              structure.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={optInPolicy.allowEmployeeOptOut}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      allowEmployeeOptOut: e.target.checked,
                    }))
                  }
                />
                Allow employee opt-out within FY
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={optInPolicy.blockOptOutIfPendingClaims}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      blockOptOutIfPendingClaims: e.target.checked,
                    }))
                  }
                />
                Block opt-out if claims are pending
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={optInPolicy.lockAfterFirstApprovedClaim}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      lockAfterFirstApprovedClaim: e.target.checked,
                    }))
                  }
                />
                Lock after first approved/paid claim
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={optInPolicy.lockAfterPayrollPosting}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      lockAfterPayrollPosting: e.target.checked,
                    }))
                  }
                />
                Lock after payroll posting
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={optInPolicy.allowAmountIncreaseInWindow}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      allowAmountIncreaseInWindow: e.target.checked,
                    }))
                  }
                />
                Allow increase declared amount (in window)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={optInPolicy.allowAmountDecreaseToClaimedFloor}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      allowAmountDecreaseToClaimedFloor: e.target.checked,
                    }))
                  }
                />
                Allow decrease (not below claimed)
              </label>
              <div>
                <label className="mb-1 block text-xs font-medium">
                  Max changes per component / FY
                </label>
                <input
                  type="number"
                  min={0}
                  disabled={!canEdit}
                  value={optInPolicy.maxChangesPerComponentPerFy}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      maxChangesPerComponentPerFy: Number(e.target.value) || 0,
                    }))
                  }
                  className={selectCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium">
                  Cool-down between changes (days)
                </label>
                <input
                  type="number"
                  min={0}
                  disabled={!canEdit}
                  value={optInPolicy.cooldownDays}
                  onChange={(e) =>
                    setOptInPolicy((p) => ({
                      ...p,
                      cooldownDays: Number(e.target.value) || 0,
                    }))
                  }
                  className={selectCls}
                />
              </div>
            </div>
          </div>

          {/* Claim windows — always editable when module on */}
          <div className="space-y-2">
            <h5 className="text-sm font-medium">Claim windows</h5>
            <p className={`text-[11px] ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Days of each month when employees can submit reimbursement claims (example: 1–20).
            </p>
            <InvestmentMonthWindowsGrid
              kind="investment"
              darkMode={darkMode}
              windows={flexiClaimWindows}
              locked={!canEdit}
              onUpdateMonth={(monthNum, patch) => {
                setFlexiClaimWindows((prev) => ({
                  ...prev,
                  months: {
                    ...(prev.months || {}),
                    [monthNum]: { ...(prev.months?.[monthNum] || {}), ...patch },
                  },
                }));
              }}
              onOpenSelection={() => {}}
              onViewSelected={() => {}}
              selectionTargetMonth={null}
              selectCls={selectCls}
            />
          </div>

          {canEdit ? (
            <DeclarationWorkflowBlock
              darkMode={darkMode}
              title="Claim approval workflow"
              value={reimbursementWorkflow}
              onChange={setReimbursementWorkflow}
            />
          ) : (
            <div className={`rounded-2xl border p-3 text-sm ${sectionShell}`}>
              <p className="mb-1 text-sm font-medium">Claim approval workflow</p>
              <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                {reimbursementWorkflow.approvalMethod === "workflow"
                  ? `${reimbursementWorkflow.workflowSteps?.length || 0} step(s) configured`
                  : "Auto approved"}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={resetSection}
          className={`rounded-full border px-4 py-2 text-sm font-medium disabled:opacity-50 ${
            darkMode
              ? "border-gray-500 bg-gray-600 text-white hover:bg-gray-550"
              : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
          }`}
        >
          Reset
        </button>
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={() => onSave?.()}
          className="rounded-full px-4 py-2 text-sm font-medium rh-btn-primary disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
});

export default ReimbursementSettings;
